import { useCallback } from 'react';

import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';
import { performHttpFetch } from '@atlassian/conversation-assistant-utils/httpUtils';

import type {
	Entity,
	GetEntityResponse,
	GetEntityResponseMap,
	GetEntityVariables,
	ListEntitiesVariables,
	ListNode,
	ListResponse,
	RecommendedSpacesResponse,
	RecommendedSpacesVariables,
	SmartRecommendedSpace,
	SmartRecommendedSpacesResponse,
	SmartRecommendedSpacesVariables,
} from './types';

const GetSingleSpaceQuery = `
query GetSpace($id: ID!, $fetchHomepage: Boolean = false) {
	confluence {
		space(id: $id) {
			id: spaceId
			name
				key
				icon {
					path
				}
			homepage @include(if: $fetchHomepage) {
			type
			ari: id
			id: pageId
			name: title
		}
		operations {
			operation
			target
		}
		typeSettings {
			enabledContentTypes {
				isBlogsEnabled
				isDatabasesEnabled
				isLivePagesEnabled
				isWhiteboardsEnabled
				isEmbedsEnabled
				isFoldersEnabled
				}
			}
		}
	}
}
`;

const GetSinglePageQuery = `
query GetPage($id: ID!) {
	confluence {
	page(id: $id) {
		id: pageId
		name: title
		space {
			id
			name
			key
			icon {
			path
			}
		}
	}
	}
}
`;

const GetSingleBlogPostQuery = `
query GetBlogPost($id: ID!) {
	confluence {
	blogPost(id: $id) {
		id: blogPostId
		name: title
	}
	}
}
`;

const ListEntitiesQuery = `
query ListEntities($query: String!, $siteAri: String!, $entityAtis: [String!]!, $confluenceFilters: SearchConfluenceFilter, $fetchHomepage: Boolean = false) {
	search {
		search(
			query: $query
			filters: {
				entities: $entityAtis,
				locations: [$siteAri],
				confluenceFilters: $confluenceFilters,
			}
			experience: "rovo-chat-message-actions"
		) {
			edges {
				node {
					type
					ari: id
					... on SearchConfluenceSpace {
						id: entityId
						name:title
						key
						iconPath
						spaceEntity {
							links {
								base
							}
							type
							operations {
								operation
								target
							}
							typeSettings {
								enabledContentTypes {
									isBlogsEnabled
									isDatabasesEnabled
									isLivePagesEnabled
									isWhiteboardsEnabled
									isEmbedsEnabled
									isFoldersEnabled
								}
							}
							homepage @include(if: $fetchHomepage) {
								type
								ari: id
								id: pageId
								name: title
							}
						}
					}
					... on SearchConfluencePageBlogAttachment {
						id:entityId
						name:title
						confluenceEntity {
							... on ConfluencePage {
								id
								subtype
							}
						}
					}
				}
			}
		}
	}
}
`;

const recommendedSpacesQuery = `
	query SmartsRecommendedContainerQueryFromRovo($userId: String!, $tenantId: String!, $fetchHomepage: Boolean = false) {
		smarts {
			results: recommendedContainer(
				recommendationsQuery: {
					context: {
						userId: $userId,
						tenantId: $tenantId,
						product: "confluence"
					},
					modelRequestParams: {
						caller: "rovo-chat-sync-space-selector",
						experience: "CgContainerNearbyUser"
					},
					requestingUserId: $userId
				}
			) {
				id
				score
				container {
					typeSettings {
						enabledContentTypes {
							isBlogsEnabled
							isDatabasesEnabled
							isLivePagesEnabled
							isWhiteboardsEnabled
							isEmbedsEnabled
							isFoldersEnabled
						}
					}
					createdDate
					id
					key
					name
					spaceId
					status
					type
					links {
						base
						webUi
					}
					icon {
						path
					}
					homepage @include(if: $fetchHomepage) {
						type
						ari: id
						id: pageId
						name: title
					}
				}
			}
		}
	}
`;

const smartRecommendedSpacesQuery = `
	query SmartsRecommendedQueryFromRovo($pageTitle: String!, $userPrompt: String!, $cloudId: ID!){
		convoai_confluenceSpaceRecommendations(
			pageTitle: $pageTitle
			userPrompt: $userPrompt
			cloudId: $cloudId
		) {
			id
			name
			spaceDetails {
				status
				type
				key
				icon {
					path
				}
				homepage {
					id
					title
					type
				}
				spaceTypeSettings {
					enabledContentTypes {
						isBlogsEnabled
						isDatabasesEnabled
						isLivePagesEnabled
						isWhiteboardsEnabled
						isEmbedsEnabled
						isFoldersEnabled
					}
				}
			}
		}
	}
`;

const getAbsoluteIconPath = (baseUrl: string, iconPath: string): string =>
	new URL(iconPath.replace(/^\//, ''), `${baseUrl.replace(/\/$/, '')}/`).toString();

/**
 * Maps a space key to its approvals space-setting config. Only the
 * disallowFirstPublish flag is currently surfaced.
 */
export type SpaceApprovalsConfigMap = Record<string, { disallowFirstPublish: boolean }>;

/**
 * The Confluence agg GraphQL API (`confluence.page` / `confluence.blogPost`) only
 * resolves published (current) content and returns `null` for drafts. Drafts can
 * only be fetched via the Confluence REST v2 API using `get-draft=true`, so we fall
 * back to it when the primary agg query returns nothing.
 */
type DraftContentRestResponse = {
	id?: string;
	title?: string;
};

const fetchDraftContentEntity = async (
	id: string,
	type: 'page' | 'blogPost',
): Promise<GetEntityResponse | undefined> => {
	const resource = type === 'page' ? 'pages' : 'blogposts';
	const draft = await performHttpFetch<DraftContentRestResponse>({
		path: `/wiki/api/v2/${resource}/${encodeURIComponent(id)}?get-draft=true`,
		options: { credentials: 'include' },
	});

	if (!draft?.id) {
		return undefined;
	}

	return {
		id: draft.id,
		name: draft.title ?? '',
	};
};

/**
 * Maximum number of unique space keys to query in a single batched
 * `confluence_contentApprovalsSpaceSettingConfig` call. The AGG gateway
 * enforces a per-request variable limit (currently around 50); going above
 * that fails the whole batch. Pickers very rarely surface more than ~25
 * spaces at once, so this cap is effectively a soft guardrail.
 */
const FETCH_SPACE_APPROVALS_BATCH_LIMIT = 50;

/**
 * Builds an aliased batch query that resolves the approvals space-setting config
 * for each space key in a single request. `confluence_contentApprovalsSpaceSettingConfig`
 * is a top-level Query field (it is not a nested field on a Confluence space), so it
 * cannot be co-resolved inside the space list queries the way typeSettings is.
 *
 * Each space key is exposed under a stable alias (s0, s1, ...). The mapping back to the
 * original space key is returned alongside the query so the response can be re-keyed.
 */
const buildSpaceApprovalsConfigsQuery = (
	spaceKeys: string[],
): { query: string; aliasToKey: Record<string, string> } => {
	const aliasToKey: Record<string, string> = {};
	const fields = spaceKeys
		.map((key, index) => {
			const alias = `s${index}`;
			aliasToKey[alias] = key;
			// spaceKey is passed as a GraphQL variable to avoid any string-injection concerns.
			return `${alias}: confluence_contentApprovalsSpaceSettingConfig(cloudId: $cloudId, spaceKey: $${alias}) {
				config {
					disallowFirstPublish
				}
			}`;
		})
		.join('\n');

	const varDecls = ['$cloudId: ID!', ...spaceKeys.map((_, index) => `$s${index}: String!`)].join(
		', ',
	);

	return {
		query: `query SpaceApprovalsConfigsFromRovo(${varDecls}) {\n${fields}\n}`,
		aliasToKey,
	};
};

export const useConfluenceEntities = (
	siteId: string,
	options?: { fetchHomepage?: boolean },
): {
	listEntities: (
		query: string,
		types: Entity[],
		spaceId?: string,
	) => Promise<
		{
			ari: string;
			id: string;
			key: string | undefined;
			name: string;
			type: string;
			homepage:
				| (ListNode & {
						type: 'page';
				  })
				| undefined;
			iconPath: string | undefined;
			subtype: string | undefined;
		}[]
	>;
	getEntity: (
		id: string,
		type: 'space' | 'page' | 'blogPost',
	) => Promise<GetEntityResponse | undefined>;
	fetchRecommendedSpaces: ({ userId }: { userId: string }) => Promise<
		{
			id: string;
			container: {
				createdDate: string;
				id: string;
				key: string;
				name: string;
				spaceId: string;
				status: string;
				type: 'GLOBAL' | 'PERSONAL';
				links: {
					base: string;
					webUi: string;
				};
				icon: {
					path: string;
				};
				homepage?: ListNode;
			};
		}[]
	>;
	fetchSmartRecommendedSpaces: ({
		pageTitle,
		userPrompt,
	}: {
		pageTitle: string;
		userPrompt: string;
	}) => Promise<SmartRecommendedSpace[]>;
	fetchSpaceApprovalsConfigs: (spaceKeys: string[]) => Promise<SpaceApprovalsConfigMap>;
} => {
	const listEntities = useCallback(
		async (
			query: string,
			types: Entity[],
			spaceId?: string,
		): Promise<
			{
				ari: string;
				id: string;
				key: string | undefined;
				name: string;
				type: string;
				homepage:
					| (ListNode & {
							type: 'page';
					  })
					| undefined;
				iconPath: string | undefined;
				subtype: string | undefined;
			}[]
		> => {
			try {
				const response = await performGqlFetch<ListResponse, ListEntitiesVariables>({
					gqlQuery: ListEntitiesQuery,
					headerOptions: {
						'X-ExperimentalApi': 'confluence-agg-beta',
					},
					variables: {
						query,
						entityAtis: types.map((type) => `ati:cloud:confluence:${type.toLowerCase()}`),
						siteAri: `ari:cloud:confluence::site/${siteId}`,
						confluenceFilters: spaceId
							? {
									containerARIs: [`ari:cloud:confluence:${siteId}:space/${spaceId}`],
								}
							: undefined,
						fetchHomepage: options?.fetchHomepage,
					},
				});

				return response.data.search.search.edges
					.filter((edge) => {
						if (edge.node.type === 'space') {
							const operations = edge.node.spaceEntity?.operations;
							return (
								operations?.some((op) => op?.operation === 'CREATE' && op?.target === 'PAGE') ??
								false
							);
						}
						return true;
					})
					.map((edge) => ({
						ari: edge.node.ari,
						id: edge.node.id,
						key: edge.node.type === 'space' ? edge.node.key : undefined,
						name: edge.node.name,
						type:
							edge.node.type === 'space'
								? (edge.node.spaceEntity?.type ?? edge.node.type)
								: edge.node.type,
						homepage: edge.node.type === 'space' ? edge.node.spaceEntity?.homepage : undefined,
						iconPath:
							edge.node.type === 'space' && edge.node.iconPath && edge.node.spaceEntity?.links?.base
								? getAbsoluteIconPath(edge.node.spaceEntity.links.base, edge.node.iconPath)
								: edge.node.type === 'space'
									? edge.node.iconPath
									: undefined,
						subtype: edge.node.type !== 'space' ? edge.node.confluenceEntity?.subtype : undefined,
						typeSettings:
							edge.node.type === 'space' ? edge.node.spaceEntity?.typeSettings : undefined,
					}));
			} catch (error) {
				logException({
					exception: error,
					name: 'ListEntitiesQueryError',
					tags: { cloudId: siteId },
				});
				return [];
			}
		},
		[siteId, options?.fetchHomepage],
	);

	const getEntity = useCallback(
		async (
			id: string,
			type: 'space' | 'page' | 'blogPost',
		): Promise<GetEntityResponse | undefined> => {
			let query: string;
			switch (type) {
				case 'space':
					query = GetSingleSpaceQuery;
					break;
				case 'page':
					query = GetSinglePageQuery;
					break;
				case 'blogPost':
					query = GetSingleBlogPostQuery;
					break;
			}
			try {
				const response = await performGqlFetch<GetEntityResponseMap, GetEntityVariables>({
					gqlQuery: query,
					variables: {
						id: `ari:cloud:confluence:${siteId}:${type.toLowerCase()}/${id}`,
						fetchHomepage: options?.fetchHomepage,
					},
					headerOptions: {
						'X-ExperimentalApi': 'confluence-agg-beta',
					},
				});

				// The agg API cannot resolve drafts, so fall back to the Confluence REST
				// v2 draft API for pages and blog posts when the primary query is empty.
				if (
					(type === 'page' && !response.data.confluence.page) ||
					(type === 'blogPost' && !response.data.confluence.blogPost)
				) {
					return await fetchDraftContentEntity(id, type);
				}

				return response.data.confluence[type];
			} catch (error) {
				logException({
					exception: error,
					name: 'GetEntityQueryError',
					tags: { cloudId: siteId },
				});
				return undefined;
			}
		},
		[siteId, options?.fetchHomepage],
	);

	const fetchRecommendedSpaces = useCallback(
		async ({
			userId,
		}: {
			userId: string;
		}): Promise<
			{
				id: string;
				container: {
					createdDate: string;
					id: string;
					key: string;
					name: string;
					spaceId: string;
					status: string;
					type: 'GLOBAL' | 'PERSONAL';
					links: {
						base: string;
						webUi: string;
					};
					icon: {
						path: string;
					};
					homepage?: ListNode;
					typeSettings?: {
						enabledContentTypes?: {
							isWhiteboardsEnabled: boolean;
							isBlogsEnabled: boolean;
							isDatabasesEnabled: boolean;
							isLivePagesEnabled: boolean;
							isEmbedsEnabled: boolean;
							isFoldersEnabled: boolean;
						};
					};
				};
			}[]
		> => {
			try {
				const response = await performGqlFetch<
					RecommendedSpacesResponse,
					RecommendedSpacesVariables
				>({
					gqlQuery: recommendedSpacesQuery,
					variables: {
						tenantId: siteId,
						userId,
						fetchHomepage: options?.fetchHomepage,
					},
				});

				return response.data.smarts.results.map((recommendedSpace) => ({
					...recommendedSpace,
					container: {
						...recommendedSpace.container,
						icon: {
							...recommendedSpace.container.icon,
							path: getAbsoluteIconPath(
								recommendedSpace.container.links.base,
								recommendedSpace.container.icon.path,
							),
						},
					},
				}));
			} catch (error) {
				logException({
					exception: error,
					name: 'RecommendedSpacesQueryError',
					tags: { cloudId: siteId },
				});
				return [];
			}
		},
		[siteId, options?.fetchHomepage],
	);

	const fetchSmartRecommendedSpaces = useCallback(
		async ({
			pageTitle,
			userPrompt,
		}: {
			pageTitle: string;
			userPrompt: string;
		}): Promise<SmartRecommendedSpace[]> => {
			try {
				const response = await performGqlFetch<
					SmartRecommendedSpacesResponse,
					SmartRecommendedSpacesVariables
				>({
					gqlQuery: smartRecommendedSpacesQuery,
					variables: {
						pageTitle,
						userPrompt,
						cloudId: siteId,
					},
				});

				return response.data.convoai_confluenceSpaceRecommendations.filter(
					({ spaceDetails }) => spaceDetails?.status.toLowerCase() === 'current',
				);
			} catch (error) {
				logException({
					exception: error,
					name: 'SmartRecommendedSpacesQueryError',
					tags: { cloudId: siteId },
				});
				return [];
			}
		},
		[siteId],
	);

	const fetchSpaceApprovalsConfigs = useCallback(
		async (spaceKeys: string[]): Promise<SpaceApprovalsConfigMap> => {
			const allUniqueKeys = Array.from(new Set(spaceKeys.filter(Boolean)));
			if (allUniqueKeys.length === 0) {
				return {};
			}

			// Cap the batch so we don't blow past the AGG gateway's per-request
			// variable limit (~50 today). Spaces beyond the cap are simply not
			// resolved this round — `filterOutFirstPublishDisallowedSpaces` falls
			// back to "include the space" when its key is missing from the map,
			// matching the rest-of-system "fail open" behaviour.
			const uniqueKeys = allUniqueKeys.slice(0, FETCH_SPACE_APPROVALS_BATCH_LIMIT);

			try {
				const { query, aliasToKey } = buildSpaceApprovalsConfigsQuery(uniqueKeys);
				const variables: Record<string, string> = { cloudId: siteId };
				uniqueKeys.forEach((key, index) => {
					variables[`s${index}`] = key;
				});

				const response = await performGqlFetch<
					{
						data: Record<string, { config?: { disallowFirstPublish?: boolean } | null } | null>;
					},
					Record<string, string>
				>({
					gqlQuery: query,
					variables,
					headerOptions: {
						'X-ExperimentalApi': 'confluence-agg-beta',
					},
				});

				const result: SpaceApprovalsConfigMap = {};
				const aliasMap: Record<
					string,
					{ config?: { disallowFirstPublish?: boolean } | null } | null
				> = response?.data ?? {};
				Object.entries(aliasToKey).forEach(([alias, key]) => {
					const disallowFirstPublish = aliasMap[alias]?.config?.disallowFirstPublish ?? false;
					result[key] = { disallowFirstPublish };
				});
				return result;
			} catch (error) {
				logException({
					exception: error,
					name: 'SpaceApprovalsConfigsQueryError',
					tags: { cloudId: siteId },
				});
				// Degrade gracefully: an empty map means no space is treated as gated,
				// so the picker behaves exactly as it does today.
				return {};
			}
		},
		[siteId],
	);

	return {
		listEntities,
		getEntity,
		fetchRecommendedSpaces,
		fetchSmartRecommendedSpaces,
		fetchSpaceApprovalsConfigs,
	};
};
